import React from "react";
import DatePicker from "react-datepicker";
import { Calendar, Pencil } from "lucide-react";
import { addDays, safeText } from "./cotizacionConstants";

export default function GeneralSection({
  numeroEfectivo,
  setNumeroSobrescrito,
  abrirConfiguracion,
  fechaCotizacion,
  setFechaCotizacion,
  setFechaVencimiento,
  vigenciaDias,
  setVigenciaDias,
  divisa,
  setDivisa,
  ivaPct,
  setIvaPct,
  incoterm,
  setIncoterm,
  vendedores,
  vendedorId,
  setVendedorId,
  setVendedorNombre,
  setVendedorCorreo,
  asunto,
  setAsunto,
  markDirty,
  DatePickerPopper,
  invalidFields = {},
}) {
  return (
    <div className="cf-grid cf-grid-6 cf-grid-general">
      <div className="cf-field">
        <label className="cf-label">
          Folio <span className="cf-required">*</span>
        </label>

        <div className="cf-input-wrap">
          <input
            className={`cf-input ${invalidFields?.folio ? "cf-invalid" : ""}`}
            value={safeText(numeroEfectivo)}
            onChange={(e) => {
              setNumeroSobrescrito?.(e.target.value);
              markDirty?.();
            }}
            placeholder="COT-0001"
          />

          <button
            type="button"
            className="cf-icon-btn"
            onClick={() => abrirConfiguracion?.()}
            title="Editar folio"
          >
            <Pencil size={16} />
          </button>
        </div>
      </div>

      <div className="cf-field">
        <label className="cf-label">
          Fecha <span className="cf-required">*</span>
        </label>

        <div className="cf-input-wrap">
          <DatePicker
            selected={fechaCotizacion}
            onChange={(d) => {
              setFechaCotizacion?.(d || new Date());
              markDirty?.();
            }}
            locale="es-dom"
            dateFormat="d MMM yyyy"
            className={`cf-input ${invalidFields?.fecha ? "cf-invalid" : ""}`}
            popperContainer={DatePickerPopper}
            popperClassName="cf-datepicker-popper"
            calendarClassName="cf-datepicker"
            showOutsideDays={false}
            showPopperArrow={false}
            popperPlacement="bottom-start"
            formatWeekDay={(name) =>
              (name?.[0] || "").toUpperCase() + (name || "").slice(1, 3)
            }
            showIcon
            toggleCalendarOnIconClick
            icon={<Calendar size={18} />}
            renderCustomHeader={({
              date,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => (
              <div className="cf-dp-header">
                <button
                  type="button"
                  className="cf-dp-nav"
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                  aria-label="Mes anterior"
                >
                  ‹
                </button>

                <span className="cf-dp-title">
                  {`${date.toLocaleString("es-ES", {
                    month: "long",
                  })} ${date.getFullYear()}`}
                </span>

                <button
                  type="button"
                  className="cf-dp-nav"
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                  aria-label="Mes siguiente"
                >
                  ›
                </button>
              </div>
            )}
          />
        </div>
      </div>

      <div className="cf-field">
        <label className="cf-label">Vigencia</label>

        <div className="cf-input-wrap">
          <select
            className="cf-select"
            value={vigenciaDias}
            onChange={(e) => {
              const days = Number(e.target.value || 1);
              setVigenciaDias?.(days);
              const nueva = addDays(fechaCotizacion || new Date(), days);
              setFechaVencimiento?.(nueva);
              markDirty?.();
            }}
          >
            <option value={1}>1 día</option>
            <option value={7}>7 días</option>
            <option value={15}>15 días</option>
            <option value={30}>30 días</option>
            <option value={45}>45 días</option>
            <option value={60}>60 días</option>
            <option value={90}>90 días</option>
          </select>
        </div>
      </div>

      <div className="cf-field">
        <label className="cf-label">Moneda</label>
        <select
          className="cf-select"
          value={divisa}
          onChange={(e) => {
            setDivisa?.(e.target.value);
            markDirty?.();
          }}
        >
          <option value="MXN">MXN - Peso Mexicano</option>
          <option value="USD">USD - Dólar Estadounidense</option>
          <option value="EUR">EUR - Euro</option>
        </select>
      </div>

      <div className="cf-field">
        <label className="cf-label">IVA (%)</label>
        <select
          className="cf-select"
          value={String(ivaPct)}
          onChange={(e) => {
            setIvaPct?.(Number(e.target.value));
            markDirty?.();
          }}
        >
          <option value="16">16%</option>
          <option value="8">8%</option>
          <option value="0">0%</option>
        </select>
      </div>

      <div className="cf-field">
        <label className="cf-label">Incoterms</label>
        <select
          className="cf-select"
          value={incoterm}
          onChange={(e) => {
            setIncoterm?.(e.target.value);
            markDirty?.();
          }}
        >
          <option value="Seleccionar">Seleccionar</option>
          <option value="EXW">EXW</option>
          <option value="FCA">FCA</option>
          <option value="CPT">CPT</option>
          <option value="CIP">CIP</option>
          <option value="DAP">DAP</option>
          <option value="DPU">DPU</option>
          <option value="DDP">DDP</option>
          <option value="FAS">FAS</option>
          <option value="FOB">FOB</option>
          <option value="CFR">CFR</option>
          <option value="CIF">CIF</option>
        </select>
      </div>

      <div className="cf-field">
        <label className="cf-label">Vendedor</label>

        <select
          className="cf-select"
          value={vendedorId || ""}
          onChange={(e) => {
            const id = e.target.value;
            setVendedorId?.(id);

            if (!id) {
              setVendedorNombre?.("");
              setVendedorCorreo?.("");
              markDirty?.();
              return;
            }

            const v = (vendedores || []).find((x) => x.id === id) || null;
            setVendedorNombre?.(v?.nombre || "");
            setVendedorCorreo?.(v?.correo || "");
            markDirty?.();
          }}
        >
          <option value="">Seleccionar vendedor</option>

          {(vendedores || []).map((v) => (
            <option key={v.id} value={v.id}>
              {v?.nombre || "Sin nombre"}
            </option>
          ))}
        </select>
      </div>

      <div className="cf-field cf-col-span-3">
        <label className="cf-label">Asunto</label>
        <input
          className="cf-input"
          value={asunto || ""}
          onChange={(e) => {
            setAsunto?.(e.target.value);
            markDirty?.();
          }}
          placeholder="Cotización de servicios / productos"
        />
      </div>
    </div>
  );
}
